import type { ForwardedRef } from 'react'
import React, { useState, useRef, useEffect } from 'react'

import type { TitleTextBubbleChunk } from 'EditorApp/types'

import type { Descendant } from 'slate'
import { createEditor, Transforms } from 'slate'
import { Slate, Editable, withReact } from 'slate-react'
import deepEqual from 'fast-deep-equal'

import letterSpacingFor from 'utils/letterSpacingFor'
import { stateToEditorFormat, editorFormatToState } from 'utils/stateHelpers'
import { withFlatText } from 'utils/slateHelpers'

interface Props {
  color: string
  fontFamily: string
  fontSize: number
  lineHeight: number
  onChange?: (chunks: TitleTextBubbleChunk[]) => void
  indentPx: number
  chunks: TitleTextBubbleChunk[]
}

interface PropsWithRef extends Props {
  innerRef: ForwardedRef<HTMLDivElement>
}

const TitleTextBubble = ({
  color,
  fontFamily,
  fontSize,
  lineHeight,
  innerRef,
  onChange,
  indentPx,
  chunks,
}: PropsWithRef) => {
  const editorValue = stateToEditorFormat(chunks)
  const [editor] = useState(() => withFlatText(withReact(createEditor())))
  const internalUpdate = useRef(false)
  const isReadOnly = !onChange

  useEffect(() => {
    if (internalUpdate.current) {
      internalUpdate.current = false
      return
    }

    editor.children = stateToEditorFormat(chunks)
    Transforms.deselect(editor)
  }, [editor, chunks])

  const handleChange = (newEditorValue: Descendant[]) => {
    if (isReadOnly || deepEqual(newEditorValue, editorValue)) {
      return
    }

    internalUpdate.current = true
    onChange(editorFormatToState(newEditorValue))
  }

  const style = {
    color,
    fontFamily,
    fontSize,
    letterSpacing: `${letterSpacingFor(fontSize)}em`,
    lineHeight: `${lineHeight}px`,
    margin: `0 0 0 ${indentPx}px`,
  }

  return (
    <div className="TitleTextBubble" style={style} ref={innerRef}>
      <Slate editor={editor} initialValue={editorValue} onChange={handleChange}>
        <Editable
          readOnly={isReadOnly}
          style={{ whiteSpace: 'pre', outline: 'none' }}
        />
      </Slate>
    </div>
  )
}

export default React.forwardRef(
  (props: Props, ref: ForwardedRef<HTMLDivElement>) => (
    <TitleTextBubble innerRef={ref} {...props} />
  )
)
